"use client";

import { RiLoginBoxLine, RiUserAddLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { memo, useCallback } from "react";

interface SignupSuggestionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onContinueWithoutSignIn: () => void;
	festivalSlug: string;
	festivalName: string;
}

export const SignupSuggestionModal = memo(function SignupSuggestionModal({
	open,
	onOpenChange,
	onContinueWithoutSignIn,
	festivalSlug,
	festivalName,
}: SignupSuggestionModalProps) {
	const router = useRouter();
	const redirectPath = `/festivals/${festivalSlug}`;

	const handleSignUp = useCallback(() => {
		onOpenChange(false);
		router.push(`/auth/signup?next=${encodeURIComponent(redirectPath)}`);
	}, [onOpenChange, router, redirectPath]);

	const handleSignIn = useCallback(() => {
		onOpenChange(false);
		router.push(`/auth/signin?next=${encodeURIComponent(redirectPath)}`);
	}, [onOpenChange, router, redirectPath]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-full max-w-[95%] p-0 sm:max-w-md">
				<DialogTitle className="sr-only">
					Personalize your wallpaper
				</DialogTitle>
				<DialogDescription className="sr-only">
					Sign up to add must-see and want-to-see markings to your schedule
					wallpaper
				</DialogDescription>
				<DialogHeader className="px-6 pt-6 pb-0">
					<h2 className="font-semibold leading-none tracking-tight">
						Make your wallpaper personal
					</h2>
					<p className="text-muted-foreground text-sm">
						Sign up to highlight your must-see and want-to-see sets on your{" "}
						{festivalName} wallpaper.
					</p>
				</DialogHeader>

				<div className="flex flex-col gap-4 px-6 pt-1 pb-6">
					<div className="overflow-hidden rounded-lg border">
						<Image
							src="/images/wallpaper-export-example.webp"
							alt="Example wallpaper with color-coded must-see and want-to-see sets"
							width={2193}
							height={1265}
							className="w-full h-auto"
							priority
						/>
					</div>

					<div className="flex gap-3">
						<Button onClick={handleSignUp} className="flex-1 min-w-0" size="lg">
							<RiUserAddLine className="h-4 w-4" />
							Sign Up
						</Button>

						<Button
							onClick={handleSignIn}
							variant="outline"
							className="flex-1 min-w-0"
							size="lg"
						>
							<RiLoginBoxLine className="h-4 w-4" />
							Sign In
						</Button>
					</div>

					<Button
						onClick={onContinueWithoutSignIn}
						variant="outline"
						className="w-full"
						size="lg"
					>
						Continue without Account
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
});
